import axios from 'axios';
import React, { useEffect, useState } from 'react';
import './App.css';
import AdminDashboard from './components/AdminDashboard';
import AppNavigationButton from './components/AppNavigationButton';
import Dashboard from './components/Dashboard';
import DocumentList from './components/DocumentList';
import DocumentUpload from './components/DocumentUpload';
import FeedbackForm from './components/FeedbackForm';
import Login from './components/Login';
import Navigation from './components/Navigation';
import OrganizationTree from './components/OrganizationTree';
import ProtectedRoute from './components/ProtectedRoute';
import Register from './components/Register';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { restoreCurrentView, saveCurrentView } from './utils/viewPersistence';

// Main App component that handles authentication routing
function AppContent() {
  const [reviews, setReviews] = useState([]);
  const [enrichedDocuments, setEnrichedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isViewRestored, setIsViewRestored] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [documentFilters, setDocumentFilters] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [viewRestored, setViewRestored] = useState(false);

  // Restore view on component mount to avoid hooks issues
  useEffect(() => {
    const restored = restoreCurrentView();
    if (restored && restored !== 'dashboard') {
      setActiveTab(restored);
      setIsViewRestored(true);
      setTimeout(() => setViewRestored(true), 100);
    }
  }, []);

  const { isAuthenticated, user, loading: authLoading } = useAuth();

  // Save current view whenever it changes
  useEffect(() => {
    if (isAuthenticated()) {
      saveCurrentView(activeTab);
    }
  }, [activeTab, isAuthenticated]);

  // Hide view restored message after 3 seconds
  useEffect(() => {
    if (viewRestored) {
      const timer = setTimeout(() => {
        setViewRestored(false);
        setIsViewRestored(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [viewRestored]);

  // Extracted fetchData function for reuse in periodic refresh
  const fetchData = async () => {
    try {
      // Fetch reviews (axios will use the token from AuthContext)
      const reviewsResponse = await axios.get('/reviews');

      // Fetch enriched documents
      const documentsResponse = await axios.get('/documents/enriched');

      if (reviewsResponse.status === 200 && documentsResponse.status === 200) {
        setReviews(reviewsResponse.data);
        setEnrichedDocuments(documentsResponse.data);
        setLoading(false);
      } else {
        setError('Error fetching data: ' + (reviewsResponse.statusText || documentsResponse.statusText));
        setLoading(false);
      }
    } catch (err) {
      // If enriched documents endpoint fails, just use regular reviews
      try {
        const reviewsResponse = await axios.get('/reviews');
        if (reviewsResponse.status === 200) {
          setReviews(reviewsResponse.data);
          setLoading(false);
        }
      } catch (fallbackErr) {
        setError('Error fetching data: ' + fallbackErr.message);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (!isAuthenticated() || authLoading) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [isAuthenticated, authLoading]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login/register if not authenticated
  if (!isAuthenticated()) {
    if (showRegister) {
      return (
        <Register onSwitchToLogin={() => setShowRegister(false)} />
      );
    } else {
      return (
        <Login onSwitchToRegister={() => setShowRegister(true)} />
      );
    }
  }

  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle navigation from Dashboard to Document Reviews with filters
  const handleNavigateToReviews = (filters) => {
    setDocumentFilters(filters);
    setActiveTab('reviews');
  };

  // Refresh data after successful upload
  const handleUploadSuccess = async (uploadResult) => {
    // Refresh reviews and documents data
    try {
      const authHeader = {
        'Authorization': `Bearer ${process.env.REACT_APP_API_AUTH_TOKEN || '12345'}`
      };

      // Fetch reviews
      const reviewsResponse = await axios.get('/reviews', {
        headers: authHeader
      });

      // Fetch enriched documents
      const documentsResponse = await axios.get('/documents/enriched', {
        headers: authHeader
      });

      if (reviewsResponse.status === 200 && documentsResponse.status === 200) {
        setReviews(reviewsResponse.data);
        setEnrichedDocuments(documentsResponse.data);
      }

      // Note: No automatic redirect here - DocumentUpload component handles the countdown and redirect
    } catch (err) {
      console.error('Error refreshing data after upload:', err);
      // Data refresh failed, but still allow manual navigation via DocumentUpload component
    }
  };

  // Handle navigation to reviews from DocumentUpload component
  const handleNavigateToReviewsFromUpload = () => {
    setActiveTab('reviews');
  };

  // Render content based on active tab
  const renderContent = () => {
    if (loading) {
      return <p>Loading reviews...</p>;
    }

    if (error) {
      return <div className="error-message">{error}</div>;
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard
          reviews={reviews}
          onNavigateToReviews={handleNavigateToReviews}
        />;
      case 'upload':
        return <DocumentUpload
          onUploadSuccess={handleUploadSuccess}
          onNavigateToReviews={handleNavigateToReviewsFromUpload}
        />;
      case 'reviews':
        return <DocumentList
          reviews={reviews}
          enrichedDocuments={enrichedDocuments}
          initialFilters={documentFilters || null}
          onFiltersCleared={() => setDocumentFilters(null)}
          onRefreshData={fetchData}
        />;
      case 'organization':
        return <OrganizationTree
          reviews={reviews}
          enrichedDocuments={enrichedDocuments}
          useApi={true}
          onDocumentSelect={(review) => {
            console.log('Selected review:', review);
            // Navigate to Document Reviews tab with filters set to show only this document
            setDocumentFilters({
              documentId: review.document_id.toString()
            });
            setActiveTab('reviews');
          }}
        />;
      case 'feedback':
        return <FeedbackForm />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return <Dashboard reviews={reviews} />;
    }
  };

  return (
    <ProtectedRoute>
      <div className={`App ${navCollapsed ? 'nav-collapsed' : ''}`}>
        <Navigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          user={user}
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          isCollapsed={navCollapsed}
          onToggleCollapsed={() => setNavCollapsed(!navCollapsed)}
        />

        <div className="main-content">
          <header className="App-header">
            <div className="header-content">
              <div className="header-left">
                <button
                  className="mobile-nav-toggle"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Open navigation menu"
                >
                  ☰
                </button>
                <img
                  src="/favicon.ico"
                  alt="S3 Lion Logo"
                  className="logo"
                />
              </div>
              <h1>Document Processing System</h1>
              <div className="header-right">
                {viewRestored && isViewRestored && (
                  <div className="view-restored-message">
                    Returned to {activeTab === 'reviews' ? 'Document Reviews' : activeTab}
                  </div>
                )}
                <div className="user-info">
                  Welcome, {user?.first_name || user?.username}!
                </div>
                <AppNavigationButton />
              </div>
            </div>
          </header>

          <main>
            {renderContent()}
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}

// Main App wrapper with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;